import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FolderLock, Upload, Download, Trash2, FileText, File as FileIcon } from "lucide-react";
import { api } from "../api";
import { PageHeader, GlassCard, Empty, Spinner, Chip } from "../components/ui";

const CATEGORIES = ["Articles of Incorporation", "Operating Agreement", "EIN Letter", "Business Licenses",
  "Insurance", "Bank Documents", "Credit Reports", "Invoices", "Vendor Documents", "Dispute Letters",
  "Tax Documents", "Certificates", "Other"];

export default function Vault() {
  const [docs, setDocs] = useState(null);
  const [category, setCategory] = useState("Other");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then(({ data }) => setDocs(data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open file"); }
  };

  const remove = async (id) => {
    setDocs((d) => d.filter((x) => x.id !== id));
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); } catch { load(); }
  };

  if (docs === null) return <Spinner />;

  const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div className="space-y-6">
      <PageHeader label="Secure Storage" title="Document Vault"
        subtitle="Securely store your business and credit documents. Download or permanently delete anytime." />

      <GlassCard className="p-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="cc-input rounded-xl px-4 py-2.5 text-sm sm:w-64" data-testid="vault-category">
            {CATEGORIES.map((c) => <option key={c} className="bg-[#1a1a24]">{c}</option>)}
          </select>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="vault-upload-btn"
            className="cc-btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-500 disabled:opacity-60">
            <Upload size={16} /> {uploading ? "Uploading…" : "Upload Document"}
          </button>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="vault-file-input" />
        </div>
      </GlassCard>

      {docs.length === 0 ? (
        <GlassCard><Empty icon={FolderLock} title="Your vault is empty" subtitle="Upload formation documents, EIN letters, licenses, insurance, and more to keep everything organized and secure." /></GlassCard>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => (
            <GlassCard key={d.id} className="p-5" data-testid={`doc-${d.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl cc-surface-2 text-blue-300">
                  {d.content_type?.includes("pdf") ? <FileText size={20} /> : <FileIcon size={20} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{d.original_filename}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtSize(d.size)}</div>
                </div>
              </div>
              <div className="mt-3"><Chip tone="blue">{d.category}</Chip></div>
              <div className="mt-4 flex items-center gap-2">
                <button onClick={() => download(d)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/10 py-2 text-xs text-slate-300 cc-nav-item" data-testid={`doc-download-${d.id}`}>
                  <Download size={14} /> Open
                </button>
                <button onClick={() => remove(d.id)} className="rounded-lg border border-white/10 px-3 py-2 text-slate-400 hover:text-red-400 cc-nav-item" data-testid={`doc-delete-${d.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
